import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Link2,
  Plus,
  Copy,
  Check,
  Trash2,
  ExternalLink,
  MousePointer,
  MapPin,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LinkTrackerPage = () => {
  const { getAuthHeader } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newName, setNewName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [linkDetails, setLinkDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchLinks = async () => {
    try {
      const response = await axios.get(`${API}/links`, {
        headers: getAuthHeader()
      });
      setLinks(response.data);
    } catch (error) {
      toast.error("Failed to fetch links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUrl || !newName) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/links`, 
        { destination_url: newUrl, name: newName },
        { headers: getAuthHeader() }
      );
      toast.success("Tracked link created");
      setDialogOpen(false);
      setNewUrl("");
      setNewName("");
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create link");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (linkId) => {
    try {
      await axios.delete(`${API}/links/${linkId}`, {
        headers: getAuthHeader()
      });
      toast.success("Link deleted");
      fetchLinks();
      if (selectedLink === linkId) {
        setSelectedLink(null);
        setLinkDetails(null);
      }
    } catch (error) {
      toast.error("Failed to delete link");
    }
  };

  const handleViewDetails = async (linkId) => {
    setSelectedLink(linkId);
    setDetailsLoading(true);
    try {
      const response = await axios.get(`${API}/links/${linkId}`, {
        headers: getAuthHeader()
      });
      setLinkDetails(response.data);
    } catch (error) {
      toast.error("Failed to fetch link details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const copyToClipboard = async (shortCode) => {
    const trackingUrl = `${process.env.REACT_APP_BACKEND_URL}/api/r/${shortCode}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(trackingUrl);
      } else {
        // Fallback for non-HTTPS
        const textArea = document.createElement("textarea");
        textArea.value = trackingUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopiedId(shortCode);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success("Link copied!");
    } catch (error) {
      // Show the URL in a toast so user can copy manually
      toast.info(`Copy this URL: ${trackingUrl}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="link-tracker-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <Link2 className="w-7 h-7 text-primary" />
            Link Tracker
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Create trackable links to monitor clicks and visitor info
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchLinks}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2" data-testid="create-link-btn">
                <Plus className="w-4 h-4" />
                Create Link
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle>Create Tracked Link</DialogTitle>
                <DialogDescription>
                  Enter a destination URL and name for your tracked link.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Link Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Newsletter CTA"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="bg-background"
                    data-testid="link-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="url">Destination URL</Label>
                  <Input
                    id="url"
                    placeholder="https://example.com/landing-page"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    className="bg-background font-mono text-sm"
                    data-testid="link-url-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="confirm-create-btn">
                    Create Link
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <p className="text-sm">
            <strong>How it works:</strong> Create a tracked link, share it anywhere (email, social media, etc.), 
            and monitor who clicks it. You'll see their IP address, location, device, and whether they're using a VPN.
          </p>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Links List */}
        <Card className="bg-card/50 border-border" data-testid="links-list">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Your Tracked Links</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="skeleton h-16 w-full" />
                ))}
              </div>
            ) : links.length > 0 ? (
              <div className="space-y-3">
                {links.map((link) => (
                  <div 
                    key={link.id}
                    className={`p-4 bg-background rounded border transition-colors cursor-pointer ${
                      selectedLink === link.id ? "border-primary" : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => handleViewDetails(link.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{link.name}</p>
                        <p className="text-xs text-muted-foreground truncate mt-1">{link.destination_url}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <Badge variant="outline" className="gap-1">
                            <MousePointer className="w-3 h-3" />
                            {link.click_count} clicks
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(link.short_code); }}
                          data-testid={`copy-link-${link.id}`}
                        >
                          {copiedId === link.short_code ? (
                            <Check className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-red-500"
                          onClick={(e) => { e.stopPropagation(); handleDelete(link.id); }}
                          data-testid={`delete-link-${link.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Link2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tracked links yet</p>
                <p className="text-sm mt-1">Create your first tracked link to get started</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Link Details */}
        <Card className="bg-card/50 border-border" data-testid="link-details">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Link Details</CardTitle>
          </CardHeader>
          <CardContent>
            {detailsLoading ? (
              <div className="space-y-2">
                <div className="skeleton h-8 w-full" />
                <div className="skeleton h-32 w-full" />
              </div>
            ) : linkDetails ? (
              <div className="space-y-4">
                {/* Link Info */}
                <div className="p-3 bg-background rounded border border-border">
                  <p className="font-medium">{linkDetails.link.name}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="text-xs bg-secondary px-2 py-1 rounded font-mono text-primary">
                      {process.env.REACT_APP_BACKEND_URL}/api/r/{linkDetails.link.short_code}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => copyToClipboard(linkDetails.link.short_code)}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {/* Country Breakdown */}
                {linkDetails.countries && linkDetails.countries.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Top Countries</p>
                    <div className="space-y-1">
                      {linkDetails.countries.map((c, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span>{c.country}</span>
                          <Badge variant="outline">{c.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Clicks */}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Recent Clicks</p>
                  {linkDetails.clicks && linkDetails.clicks.length > 0 ? (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {linkDetails.clicks.slice(0, 10).map((click, i) => (
                        <div key={i} className="p-2 bg-background rounded border border-border text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-primary">{click.ip_address}</span>
                            <div className="flex gap-1">
                              {click.is_vpn && (
                                <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px]">VPN</Badge>
                              )}
                              {click.is_proxy && (
                                <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px]">Proxy</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            {click.city && click.country ? `${click.city}, ${click.country}` : click.country || "Unknown"}
                            <span>•</span>
                            {click.browser}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(click.timestamp).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No clicks yet</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <MousePointer className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a link to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LinkTrackerPage;
